import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { WidgetCard } from "@/components/bi/WidgetCard";
import { WidgetRenderer } from "@/components/bi/WidgetRenderer";
import { ContainerBody } from "@/components/bi/ContainerWidget";
import { minSizeFor } from "@/lib/layoutEngine";
import { FilterBar, toActiveFilters, clearDescendants, defaultFilterValues } from "@/components/bi/FilterBar";
import { useFilterSets } from "@/hooks/useFilterSets";
import { FilterPanel, FilterPanelToggle } from "@/components/bi/FilterPanel";
import { inPanel, downloadFilteredCsv } from "@/lib/filterPanel";
import { DashboardTabs } from "@/components/bi/DashboardTabs";
import {
  normalizeTabs, normalizeBar, initialTabId, rememberTab, widgetTabId,
  tabCanvas, openableTabs, defaultTabId, saveTabScroll, readTabScroll,
} from "@/lib/dashboardTabs";
import { FILTER_WIDGET_TYPES, filterWidgetToFilters } from "@/components/bi/FilterWidgets";
import { MobileFilterSheet } from "@/components/bi/MobileFilterSheet";
import { Loading } from "@/components/bi/Loading";
import { EmptyState } from "@/components/bi/EmptyState";
import { viewerGateScreen } from "@/components/bi/ViewerGates";
import { WidgetErrorBoundary, DashboardErrorBoundary } from "@/components/bi/ErrorBoundaries";
import { interactionFilters, splitInteractionFilters, slicerFilters, isCrossDatasetWidget } from "@/lib/widgetCatalog";
import { WidgetViewActions } from "@/components/bi/WidgetViewActions";
import { UnderlyingDataDialog } from "@/components/bi/UnderlyingDataDialog";
import { setFormatLocale } from "@/lib/chartTheme";
import { applyRegional } from "@/context/RegionalContext";
import { decodeDrill } from "@/lib/drillthrough";
import { DrillthroughBar } from "@/components/bi/DrillthroughBar";
import { registerNavState, readNavState, applyScroll, setModalChrome } from "@/lib/navState";
import { loadFilterState, saveFilterState } from "@/lib/filterStatePersist";
import { setLanguage, t, isRTL } from "@/lib/i18n";
import { normalizeCanvas, isFixedCanvas, mobileLayoutFor, computeFitScale, effectiveWhatIfParams } from "@/lib/canvasUtils";
import { WhatIfBar } from "@/components/bi/WhatIfBar";
import { paramFilters, resolveParamTokens } from "@/lib/paramEngine";
import api, { setShareAccessToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ProfileSwitcher, applyProfilePatch } from "@/components/bi/ProfileSwitcher";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { exportPNG, exportPDF } from "@/lib/exportUtils";
import { toast } from "sonner";
import { Sparkles, Lock, Download, FileImage, FileText, KeyRound, Clock, Ban, Loader2 } from "lucide-react";

export default function Viewer({ embed = false }) {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(false);
  const [gate, setGate] = useState(null);      // { password_required, expired, domain_ok, kind }
  const [pw, setPw] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [gfVals, setGfVals] = useState({});
  const [slicerVals, setSlicerVals] = useState({});
  const [fwVals, setFwVals] = useState({});
  const [underlying, setUnderlying] = useState(null);   // item 26: widget detail in published / shared views
  // Item 4: this viewer's last applied filter state for this dashboard (per user, local only).
  const fsRestoredRef = useRef(null);
  useEffect(() => {
    const id = dashboard?.id;
    if (!id || fsRestoredRef.current === id) return;
    fsRestoredRef.current = id;
    const st = loadFilterState(id);
    if (!st) return;
    if (st.gf) setGfVals((v) => ({ ...v, ...st.gf }));
    if (st.sl) setSlicerVals(st.sl);
    if (st.fw) setFwVals(st.fw);
  }, [dashboard?.id]);
  useEffect(() => {
    const id = dashboard?.id;
    if (!id || fsRestoredRef.current !== id) return;
    saveFilterState(id, { gf: gfVals, sl: slicerVals, fw: fwVals });
  }, [dashboard?.id, gfVals, slicerVals, fwVals]);
  const [whatIfVals, setWhatIfVals] = useState({});
  const [whatIfCompare, setWhatIfCompare] = useState(false);
  const [crossFilters, setCrossFilters] = useState({});
  // Item 13: drillthrough context arriving from another (public) dashboard via ?dt=
  const [drillIn, setDrillIn] = useState(() => decodeDrill(new URLSearchParams(window.location.search).get("dt")));
  const clearDrillIn = () => {
    setDrillIn(null);
    const u = new URL(window.location.href);
    u.searchParams.delete("dt");
    window.history.replaceState({}, "", u.pathname + u.search);
  };
  const [refreshToken, setRefreshToken] = useState(0);
  // Item 194.5: embed display configuration via URL params
  const emb = {
    hideFilters: searchParams.get("hideFilters") === "1",
    noInteract: searchParams.get("noInteract") === "1",
    transparent: searchParams.get("transparent") === "1",
  };
  const toggleCross = (wid, datasetId, dimRef, value) => {
    setCrossFilters((cf) => {
      const cur = cf[wid];
      if (cur && JSON.stringify(cur.value) === JSON.stringify(value) && cur.field === dimRef.field && cur.key === dimRef.key) { const n = { ...cf }; delete n[wid]; return n; }
      return { ...cf, [wid]: { dataset_id: datasetId, field: dimRef.field, key: dimRef.key, value } };
    });
  };

  // Responsive: dedicated mobile experience below 768px
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [panelOpen, setPanelOpen] = useState(() => window.innerWidth >= 860);
  const [narrow, setNarrow] = useState(() => window.innerWidth < 860);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 860);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => { if (narrow) setPanelOpen(false); }, [narrow]);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const fn = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  // Container measurement for fit-to-screen scaling on any resolution
  const containRef = useRef(null);
  const innerRef = useRef(null);
  const [containerW, setContainerW] = useState(window.innerWidth);
  const [innerH, setInnerH] = useState(600);
  useEffect(() => {
    const el = containRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setContainerW(el.clientWidth));
    ro.observe(el); setContainerW(el.clientWidth);
    return () => ro.disconnect();
  }, [dashboard]);
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setInnerH(el.offsetHeight));
    ro.observe(el); setInnerH(el.offsetHeight);
    return () => ro.disconnect();
  }, [dashboard, isMobile]);

  // Restore saved access token (if user previously verified this token in this browser tab).
  useEffect(() => {
    if (!token) return;
    const stored = sessionStorage.getItem(`share_access_${token}`);
    if (stored) setShareAccessToken(stored);
    else setShareAccessToken(null);
  }, [token]);

  const loadGate = async () => {
    try {
      const { data } = await api.get(`/public/dashboard/${token}/gate`);
      setGate(data);
      return data;
    } catch (e) {
      setError(true);
      return null;
    }
  };

  const loadDashboard = async () => {
    try {
      const { data } = await api.get(`/public/dashboard/${token}`);
      setDashboard(data);
      if (data.regional) applyRegional({ ...data.regional, dictionary: data.dictionary }); // item 20: owner tenant's regional defaults + org dictionary
      const b = data.branding || {};
      const root = document.documentElement;
      if (b.primary_color) root.style.setProperty("--brand-ocean", b.primary_color);
      if (b.accent_color) root.style.setProperty("--brand-mint", b.accent_color);
      root.setAttribute("data-theme", b.theme === "dark" ? "dark" : "light");
      const defs = data.global_filters || [];
      const init = defaultFilterValues(defs);
      for (const f of defs) {
        const raw = searchParams.get(`f_${f.field}`);
        if (raw == null) continue;
        if (f.type === "multiselect") init[f.id] = raw.split(",").map((s) => s.trim()).filter(Boolean);
        else if (f.type === "daterange") { const [a, b] = raw.split(","); init[f.id] = [a || "", b || ""]; }
        else init[f.id] = raw;
      }
      setGfVals(init);
    } catch (e) {
      // 401 → password required; 410 → expired; 403 → domain blocked
      const status = e?.response?.status;
      if (status === 401) {
        setGate((g) => ({ ...(g || {}), password_required: true, verified: false }));
      } else if (status === 410 || status === 403) {
        await loadGate(); // refresh gate state so the UI shows the right message
      } else {
        setError(true);
      }
    }
  };

  useEffect(() => {
    (async () => {
      const g = await loadGate();
      if (!g) return;
      if (!g.domain_ok || g.expired) return; // block
      if (g.password_required && !g.verified) return; // wait for password
      await loadDashboard();
    })();
    // intentional: gate + dashboard load run once per share token
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitPassword = async () => {
    if (!pw) { setPwError("Enter the password"); return; }
    setPwSubmitting(true); setPwError("");
    try {
      const { data } = await api.post("/public/dashboard/verify-password", { token, password: pw });
      const access = data.access_token;
      sessionStorage.setItem(`share_access_${token}`, access);
      setShareAccessToken(access);
      setPw("");
      const g = await loadGate();
      if (g?.verified) await loadDashboard();
    } catch (e) {
      setPwError(e?.response?.data?.detail || "Incorrect password");
    } finally { setPwSubmitting(false); }
  };

  const widgets = useMemo(() => (dashboard?.widgets || []).filter((w) => !w.hidden), [dashboard]);
  const gfDefAll = useMemo(() => dashboard?.global_filters || [], [dashboard]);
  const canvasAll = useMemo(() => normalizeCanvas(dashboard?.canvas), [dashboard?.canvas]);
  // Item 97 \u2014 tabs. The public payload only carries tabs this viewer may open.
  const tabs = useMemo(() => normalizeTabs(dashboard || {}), [dashboard]);
  const tabBar = useMemo(() => normalizeBar(canvasAll), [canvasAll]);
  const [activeTab, setActiveTab] = useState("");
  const tabInitRef = useRef(null);
  useEffect(() => {
    if (!dashboard?.id || tabInitRef.current === dashboard.id) return;
    tabInitRef.current = dashboard.id;
    setActiveTab(initialTabId(dashboard.id, tabs, tabBar, searchParams.get("tab") || dashboard.tab_default || ""));
  }, [dashboard, tabs, tabBar, searchParams]);
  const viewTabs = useMemo(() => openableTabs(tabs, { gfVals, params: whatIfVals }, false), [tabs, gfVals, whatIfVals]);
  const effTab = viewTabs.some((t) => t.id === activeTab) ? activeTab : defaultTabId(viewTabs.length ? viewTabs : tabs);
  const curTab = tabs.find((t) => t.id === effTab) || tabs[0];
  const topWidgets = useMemo(() => widgets.filter((w) => !w.parent_id && widgetTabId(w, tabs) === effTab), [widgets, tabs, effTab]);
  const canvas = useMemo(() => tabCanvas(canvasAll, curTab), [canvasAll, curTab]);
  // Item 97 \u00a720: tab-scoped filters only exist while their tab is open.
  const gfDef = useMemo(() => gfDefAll.filter((f) => !f.tab_id || f.tab_id === effTab), [gfDefAll, effTab]);
  const selectTab = (tid) => {
    if (!tid || tid === effTab) return;
    saveTabScroll(token, effTab, window.scrollY);
    setActiveTab(tid);
    if (dashboard?.id) rememberTab(dashboard.id, tid);
    const u = new URL(window.location.href);
    u.searchParams.set("tab", tid);
    window.history.replaceState({}, "", u.pathname + u.search);
    const y = readTabScroll(token, tid);
    setTimeout(() => window.scrollTo({ top: y, behavior: "auto" }), 30);
  };
  // Batch 8: viewers can adjust what-if params, switch named scenarios and compare vs baseline
  const whatIfDefs = useMemo(() => canvas.what_if || [], [canvas.what_if]);
  const whatIfEff = effectiveWhatIfParams(whatIfDefs, whatIfVals);
  const whatIfBase = effectiveWhatIfParams(whatIfDefs, {});
  // Scenario share links: /view/{token}?scenario={id|name} opens preset to that scenario
  useEffect(() => {
    const sc = searchParams.get("scenario");
    if (!sc || !dashboard) return;
    const list = normalizeCanvas(dashboard.canvas).scenarios || [];
    const hit = list.find((s) => s.id === sc || s.name === sc
      || String(s.name || "").toLowerCase() === sc.toLowerCase());
    if (hit) setWhatIfVals({ ...(hit.values || {}) });
    // run once when the dashboard payload arrives
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard?.id]);
  useEffect(() => { setFormatLocale(canvas.locale); setLanguage(canvas.language); }, [canvas.locale, canvas.language, dashboard?.regional?.language]);
  // Item 13: publish a snapshot for state-preserving drillthrough Back + restore one on arrival
  useEffect(() => registerNavState(() => ({ gf: gfVals, sl: slicerVals, fw: fwVals, y: window.scrollY })), [gfVals, slicerVals, fwVals]);
  const restoreKey = new URLSearchParams(window.location.search).get("restore");
  useEffect(() => {
    if (!restoreKey) return;
    const st = readNavState(restoreKey);
    if (st) {
      if (st.gf) setGfVals(st.gf);
      if (st.sl) setSlicerVals(st.sl);
      if (st.fw) setFwVals(st.fw);
      applyScroll(st.y);
    }
    const u = new URL(window.location.href);
    u.searchParams.delete("restore");
    window.history.replaceState({}, "", u.pathname + u.search);
  }, [restoreKey]);
  useEffect(() => {
    const modal = new URLSearchParams(window.location.search).get("dtmodal") === "1";
    setModalChrome(modal);
    return () => setModalChrome(false);
  }, []);
  const rtl = canvas.language === "ar" || (!canvas.language && isRTL());
  const fixed = isFixedCanvas(canvas);
  const cols = canvas.grid_cols;

  // Same clamp as the Builder grid so a published dashboard renders exactly like the canvas.
  const gridLayout = useMemo(() => topWidgets.map((w) => {
    const min = minSizeFor(w.type);
    return { i: w.id, x: w.layout?.x || 0, y: w.layout?.y || 0, w: Math.max(min.w, w.layout?.w || 6), h: Math.max(min.h, w.layout?.h || 5), static: true };
  }), [topWidgets]);
  const mobileMap = useMemo(() => mobileLayoutFor(topWidgets), [topWidgets]);
  const mobileVisible = topWidgets.filter((w) => !w.hidden_mobile);
  const mobileGridLayout = mobileVisible.map((w) => {
    const l = mobileMap[w.id] || { x: 0, y: 0, w: 4, h: 4 };
    return { i: w.id, x: l.x, y: l.y, w: Math.min(4, l.w || 4), h: l.h || 4, static: true };
  });

  // Item 97 \u00a721: interactions stay inside the open tab unless the author allowed cross-tab ones.
  const ixWidgets = useMemo(() => {
    if (tabBar.cross_tab_interactions) return widgets;
    const own = new Set(widgets.filter((w) => !w.parent_id && widgetTabId(w, tabs) === effTab).map((w) => w.id));
    return widgets.filter((w) => own.has(w.id) || own.has(w.parent_id));
  }, [widgets, tabs, effTab, tabBar.cross_tab_interactions]);

  const activeFilters = useMemo(() => {
    const fromBar = toActiveFilters(gfDef, gfVals);
    const ixIds = new Set(ixWidgets.map((w) => w.id));
    const fromSlicers = slicerFilters(slicerVals, ixWidgets);
    const fromCross = Object.entries(crossFilters).filter(([wid]) => ixIds.has(wid)).map(([wid, cf]) => ({ dataset_id: cf.dataset_id, field: cf.field, key: cf.key, op: Array.isArray(cf.value) ? "in" : "eq", value: cf.value, __src: wid }));
    const fromFilterWidgets = ixWidgets.filter((w) => FILTER_WIDGET_TYPES.includes(w.type)).flatMap((w) => filterWidgetToFilters(w, fwVals[w.id]));
    const fromDrill = (drillIn?.f || []).map((f) => ({ ...f, __dt: true }));
    // Item 13: "override" replaces this dashboard's own filter values on the carried fields
    const overridden = drillIn?.mode === "override" ? new Set(fromDrill.map((f) => f.field)) : null;
    const keep = (list) => (overridden ? list.filter((f) => !overridden.has(f.field)) : list);
    // Item 30: parameters bound to a field filter the whole dashboard
    const fromParams = paramFilters(whatIfDefs, whatIfVals);
    return [...keep(fromParams), ...keep(fromBar), ...keep(fromSlicers), ...fromCross, ...keep(fromFilterWidgets), ...fromDrill];
  }, [gfDef, gfVals, slicerVals, crossFilters, fwVals, ixWidgets, drillIn, whatIfDefs, whatIfVals]);

  // Item 194.9/10: embed SDK bridge — auto-height, events out, commands in (postMessage).
  const gfDefRef = useRef([]);
  const gfValsRef = useRef({});
  useEffect(() => { gfValsRef.current = gfVals; });
  useEffect(() => {
    if (!embed || !dashboard || window.parent === window) return;
    gfDefRef.current = dashboard.global_filters || [];
    const post = (type, payload = {}) => { try { window.parent.postMessage({ source: "eazybi", type, ...payload }, "*"); } catch { /* sandboxed */ } };
    post("loaded", { dashboard: { id: dashboard.id, name: dashboard.name } });
    const sendHeight = () => post("height", { height: Math.max(document.documentElement.scrollHeight, document.body.scrollHeight) });
    sendHeight();
    const ro = new ResizeObserver(sendHeight);
    ro.observe(document.body);
    const onMsg = (e) => {
      const m = e.data;
      if (!m || m.target !== "eazybi") return;
      if (m.type === "applyFilters" && m.values && typeof m.values === "object") {
        const next = {};
        gfDefRef.current.forEach((f) => {
          if (Object.prototype.hasOwnProperty.call(m.values, f.field)) next[f.id] = m.values[f.field];
        });
        setGfVals((s) => ({ ...s, ...next }));
      } else if (m.type === "clearFilters") {
        setGfVals({}); setSlicerVals({}); setCrossFilters({});
      } else if (m.type === "refresh") {
        setRefreshToken((t) => t + 1);
      } else if (m.type === "getFilters") {
        const byField = {};
        gfDefRef.current.forEach((f) => { if (gfValsRef.current[f.id] != null) byField[f.field] = gfValsRef.current[f.id]; });
        post("filters", { values: byField, requestId: m.requestId });
      }
    };
    window.addEventListener("message", onMsg);
    return () => { ro.disconnect(); window.removeEventListener("message", onMsg); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed, dashboard?.id]);
  useEffect(() => {
    if (!embed || !dashboard || window.parent === window) return;
    const byField = {};
    (dashboard.global_filters || []).forEach((f) => { if (gfVals[f.id] != null) byField[f.field] = gfVals[f.id]; });
    try { window.parent.postMessage({ source: "eazybi", type: "filterChanged", values: byField }, "*"); } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embed, JSON.stringify(gfVals)]);

  const filterSets = useFilterSets({
    dashboardId: dashboard?.id, publicToken: token || null, tabId: effTab, values: gfVals,
    onApply: (vals) => { setGfVals(vals); setCrossFilters({}); }, enabled: !!(dashboard?.id || token),
  });

  const gateEl = viewerGateScreen({ error, gate, pw, setPw, pwError, pwSubmitting, submitPassword });
  if (gateEl) return gateEl;

  if (!dashboard) return <div className="min-h-screen flex items-center justify-center bi-canvas"><Loading label={t("loading_dashboard")} /></div>;

  const renderWidget = (w) => {
    if (w.type === "container") {
      return (
        <WidgetCard testid="canvas-widget" widgetType="container" title={resolveParamTokens(w.title, whatIfEff) || undefined}
          subtitle={w.config?.options?.subtitle || undefined}
          titleStyle={w.config?.options?.titleStyle}
          hideHeader={!!w.config?.options?.hideHeader}
          appearance={w.config?.options?.appearance}
          ariaLabel={w.config?.options?.altText || w.title || "Container"}>
          <ContainerBody widget={w} editing={false}
            childWidgets={widgets.filter((x) => x.parent_id === w.id)}
            renderChild={(k) => renderWidget(k)} />
        </WidgetCard>
      );
    }
    // drillthrough context only applies to widgets whose dataset actually has the field
    const dsFields = dashboard.dataset_fields || null;
    const wFilters = drillIn
      ? activeFilters.filter((f) => !f.__dt || !dsFields || !w.dataset_id || !dsFields[w.dataset_id] || dsFields[w.dataset_id].includes(f.field)).map(({ __dt, ...r }) => r)
      : activeFilters;
    const split = splitInteractionFilters(w, wFilters, widgets, dsFields, isCrossDatasetWidget(w));
    const mfValues = w.type === "multi_filter" && w.dataset_id
      ? Object.fromEntries(Object.entries(slicerVals).filter(([k]) => k.startsWith(`${w.dataset_id}::`)).map(([k, v]) => [k.split("::")[1], v]))
      : undefined;
    return (
    <WidgetCard testid="canvas-widget" widgetType={w.type} title={resolveParamTokens(w.title, whatIfEff) || undefined}
      subtitle={w.config?.options?.subtitle || undefined}
      titleStyle={w.config?.options?.titleStyle}
      subtitleStyle={w.config?.options?.subtitleStyle}
      description={w.config?.options?.description || undefined}
      hideHeader={!!w.config?.options?.hideHeader}
      appearance={w.config?.options?.appearance}
      actions={!emb.noInteract && (
        <WidgetViewActions widget={w} filters={[...(w.config?.filters || []), ...split.query.filter((f) => f && f.field)]}
          publicToken={token} whatIfParams={whatIfEff} crossDataset={isCrossDatasetWidget(w)}
          canExport={dashboard.allow_export !== false}
          onUnderlying={(w2, fls) => setUnderlying({ widget: w2, filters: fls, publicToken: token })} />
      )}
      ariaLabel={w.config?.options?.altText || w.title || w.type}>
      <WidgetErrorBoundary dashboardId={dashboard.id} widgetId={w.id} widgetType={w.type} resetKey={JSON.stringify(w.config || {})}>
        <WidgetRenderer widget={w} globalFilters={split.query} highlightFilters={split.highlight} publicToken={token} chartFont={canvas.chart_font} lang={canvas.language}
          dashboardName={dashboard?.name} drillTrail={drillIn?.trail || null}
          pivotEngine={canvas.pivot_engine}
          dashboardId={dashboard.id}
          refreshToken={refreshToken}
          whatIfParams={whatIfEff}
          whatIfBaseline={whatIfBase}
          whatIfCompare={whatIfCompare}
          whatIfDefs={whatIfDefs}
          whatIfScenarios={canvas.scenarios || []}
          multiFilterValues={mfValues}
          slicerValue={slicerVals[`${w.dataset_id}::${w.config?.dimensions?.[0]?.field}`]}
          filterValue={fwVals[w.id]}
          onFilterChange={(val) => setFwVals((s) => ({ ...s, [w.id]: val }))}
          onSlicerChange={(field, value) => setSlicerVals((s) => ({ ...s, [`${w.dataset_id}::${field}`]: value }))}
          onCrossFilter={emb.noInteract ? null : (dimRef, value) => toggleCross(w.id, w.dataset_id, dimRef, value)} />
      </WidgetErrorBoundary>
    </WidgetCard>
    );
  };

  const crossBar = Object.keys(crossFilters).length > 0 && (
    <div data-testid="crossfilter-bar" className="px-3 py-1.5 flex items-center gap-2 flex-wrap">
      <span className="text-[11px] text-[color:var(--ink-3)]">Cross-filters</span>
      {Object.entries(crossFilters).map(([wid, cf]) => (
        <span key={wid} data-testid="crossfilter-chip" className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[color:var(--surface)] border border-[color:rgba(43,78,255,0.35)] text-[color:var(--ink-2)]">
          <span className="font-medium">{cf.key || cf.field}</span> = {String(cf.value)}
          <button onClick={() => setCrossFilters((c) => { const n = { ...c }; delete n[wid]; return n; })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] ml-0.5">✕</button>
        </span>
      ))}
    </div>
  );

  // Fit-to-screen scale for fixed-size canvases (works on 1366×768 → 4K/ultrawide)
  const scale = fixed && !isMobile ? computeFitScale(canvas, Math.max(320, containerW - 16), window.innerHeight - 140) : 1;
  const gridW = fixed ? canvas.width : Math.max(320, containerW);

  const desktopGrid = (
    <div ref={containRef} className="p-2 overflow-x-auto">
      {crossBar}
      {widgets.length === 0 ? <EmptyState icon={Sparkles} title="Nothing here yet" description="This dashboard has no widgets." /> : (
        <div style={fixed ? { width: Math.round(gridW * scale), height: Math.round(innerH * scale) } : undefined} className={fixed ? "mx-auto" : undefined}>          <div id="dashboard-capture" ref={innerRef}
            role={tabs.length > 1 ? "tabpanel" : undefined}
            aria-label={tabs.length > 1 ? `${curTab?.name} tab` : undefined}
            style={{
              transform: scale !== 1 ? `scale(${scale})` : undefined, transformOrigin: "top left",
              width: fixed ? gridW : undefined, minHeight: fixed ? canvas.height : undefined,
              backgroundColor: canvas.bg_color || undefined,
            }}
            className={fixed ? "relative rounded-xl border border-[color:var(--glass-border)] shadow-[var(--shadow-md)] bg-[color:var(--canvas)] overflow-hidden" : "relative"}>
            {canvas.wallpaper_url && (
              <div aria-hidden className="absolute inset-0 pointer-events-none bg-cover bg-center"
                style={{ backgroundImage: `url(${canvas.wallpaper_url})`, opacity: canvas.wallpaper_opacity }} />
            )}
            <GridLayout key={effTab} className="layout" layout={gridLayout} width={gridW} cols={cols}
              rowHeight={56} margin={[16, 16]} containerPadding={[16, 16]}
              compactType={canvas.layout_mode === "free" ? null : "vertical"}
              isDraggable={false} isResizable={false}>
              {topWidgets.map((w) => <div key={w.id} id={`wcap-${w.id}`}>{renderWidget(w)}</div>)}
            </GridLayout>
          </div>
        </div>
      )}
    </div>
  );

  const mobileGrid = (
    <div ref={containRef} dir={rtl ? "rtl" : undefined} className="pb-20" id="dashboard-capture" style={{ backgroundColor: canvas.bg_color || undefined }}>
      {crossBar}
      {mobileVisible.length === 0 ? <EmptyState icon={Sparkles} title="Nothing here yet" description="This dashboard has no widgets." /> : (
        <GridLayout key={effTab} className="layout" layout={mobileGridLayout} width={Math.max(320, containerW)} cols={4}
          rowHeight={48} margin={[10, 10]} containerPadding={[10, 10]}
          compactType="vertical" isDraggable={false} isResizable={false}>
          {mobileVisible.map((w) => <div key={w.id}>{renderWidget(w)}</div>)}
        </GridLayout>
      )}
    </div>
  );

  const grid = isMobile ? mobileGrid : desktopGrid;

  const clearAll = () => { setGfVals({}); setSlicerVals({}); setCrossFilters({}); };
  const onGfChange = (fid, v) => setGfVals((s) => clearDescendants(gfDef, { ...s, [fid]: v }, fid));

  const goDrillStep = (step) => {
    if (!step?.path) return;
    const sep = step.path.includes("?") ? "&" : "?";
    window.location.assign(`${step.path}${step.bk ? `${sep}restore=${step.bk}` : ""}`);
  };
  const drillBack = () => {
    const last = (drillIn?.trail || [])[(drillIn?.trail || []).length - 1];
    if (last?.path) return goDrillStep(last);
    return window.history.back();
  };
  const drillBar = <DrillthroughBar drill={drillIn} onClear={clearDrillIn}
    onBack={drillIn && ((drillIn.trail || []).length > 0 || window.history.length > 1) ? drillBack : null} onStep={goDrillStep} />;

  // Item 98 \u2014 filter panel (sidebar on desktop, drawer on phones)
  const filterLayout = canvasAll.filter_layout || "bar";
  const panelSettings = canvasAll.filter_panel || {};
  const showFilterPanel = (filterLayout === "panel" || filterLayout === "both") && gfDef.some(inPanel);
  const showFilterBar = filterLayout !== "panel";
  const panelDatasetId = (gfDef.find(inPanel) || {}).dataset_id || null;
  const panelEl = showFilterPanel ? (
    <FilterPanel filtersDef={gfDef} values={gfVals} onChange={onGfChange} onClearAll={clearAll}
      publicToken={token} settings={panelSettings} tabId={effTab}
      open={panelOpen} onOpenChange={setPanelOpen} drawer={narrow}
      sets={filterSets.sets} onApplySet={filterSets.applySet}
      onSaveSet={filterSets.saveSet} onDeleteSet={filterSets.deleteSet}
      onApplyAll={(vals) => setGfVals(vals)}
      onExportCsv={panelDatasetId && dashboard.allow_export !== false ? () => toast.promise(
        downloadFilteredCsv({ datasetId: panelDatasetId, defs: gfDef.filter(inPanel), values: gfVals, publicToken: token, filename: `${dashboard.name}_filtered.csv` }),
        { loading: "Building filtered CSV\u2026", success: "Filtered CSV downloaded", error: "Export failed" }) : null} />
  ) : null;

  const filterBar = gfDef.length > 0 && !isMobile && showFilterBar && (
    <div className={`px-4 border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] ${canvas.filter_bar?.sticky ? "sticky top-0 z-40 backdrop-blur-md" : ""}`}>
      <FilterBar filtersDef={gfDef} values={gfVals} onChange={onGfChange} publicToken={token} onClearAll={clearAll}
        settings={canvas.filter_bar || {}} onApplyAll={(vals) => setGfVals(vals)} />
    </div>
  );

  const mobileFilters = isMobile && !showFilterPanel && (
    <MobileFilterSheet filtersDef={gfDef} values={gfVals} onChange={onGfChange} onClearAll={clearAll}
      publicToken={token} activeCount={toActiveFilters(gfDef, gfVals).length} />
  );

  const tabsEl = tabBar.show !== false && viewTabs.length > 1 ? (
    <DashboardTabs dashboardId={dashboard.id} tabs={viewTabs} activeId={effTab} onSelect={selectTab}
      bar={tabBar} mobile={isMobile} />
  ) : null;
  const vertBar = tabBar.position === "left" || tabBar.position === "right";
  const body = (vertBar || (showFilterPanel && !narrow)) ? (
    <div className="flex items-start gap-2">
      {vertBar && tabBar.position === "left" && tabsEl}
      {showFilterPanel && !narrow && <div className="shrink-0 py-2 pl-2">{panelEl}</div>}
      <div className="flex-1 min-w-0">{grid}</div>
      {vertBar && tabBar.position === "right" && tabsEl}
    </div>
  ) : grid;
  const stage = (
    <>
      {!vertBar && tabBar.position !== "bottom" && tabsEl}
      {body}
      {!vertBar && tabBar.position === "bottom" && tabsEl}
      {showFilterPanel && narrow && panelEl}
    </>
  );

  const whatIfBar = whatIfDefs.length > 0 && (
    <WhatIfBar defs={whatIfDefs} values={whatIfVals} onChange={setWhatIfVals} editable={false}
      scenarios={canvas.scenarios || []} compare={whatIfCompare} onCompareChange={setWhatIfCompare}
      getScenarioLink={(s) => `${window.location.origin}/view/${token}?scenario=${encodeURIComponent(s.id || s.name)}`} />
  );

  if (embed) {
    return (
      <DashboardErrorBoundary dashboardId={dashboard.id}>
      <div className="min-h-screen bi-canvas" style={emb.transparent ? { background: "transparent" } : undefined}>
        {drillBar}
        {!emb.hideFilters && filterBar}
        {!emb.hideFilters && whatIfBar}
        {emb.hideFilters ? stage : (
          <>
            {showFilterPanel && (!panelOpen || narrow) && (
              <div className="px-3 pt-2"><FilterPanelToggle open={panelOpen} onOpenChange={setPanelOpen} drawer={narrow}
                count={toActiveFilters(gfDef.filter(inPanel), gfVals).length} /></div>
            )}
            {stage}
          </>
        )}
        {!emb.hideFilters && mobileFilters}
      </div>
      </DashboardErrorBoundary>
    );
  }

  return (
    <DashboardErrorBoundary dashboardId={dashboard.id}>
    <div className="min-h-screen bi-canvas">
      <header className="h-16 px-4 sm:px-6 flex items-center justify-between border-b border-[color:var(--glass-border)] bg-[color:var(--glass-bg)] backdrop-blur-md sticky top-0 z-40">
        <div className="min-w-0">
          <h1 className="font-display text-lg sm:text-xl text-[color:var(--ink)] truncate">{dashboard.name}</h1>
          {dashboard.description && <p className="text-xs text-[color:var(--ink-3)] truncate">{dashboard.description}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {showFilterPanel && (!panelOpen || narrow) && (
            <FilterPanelToggle open={panelOpen} onOpenChange={setPanelOpen} drawer={narrow}
              count={toActiveFilters(gfDef.filter(inPanel), gfVals).length} />
          )}
          {!emb.noInteract && (
            <ProfileSwitcher widgets={widgets} onApply={(wid, pid) => setDashboard((d) => ({
              ...d,
              widgets: (d.widgets || []).map((x) => {
                if (x.id !== wid) return x;
                const patch = applyProfilePatch(x, pid);
                return patch ? { ...x, config: { ...(x.config || {}), ...patch } } : x;
              }),
            }))} />
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button data-testid="viewer-export-btn" variant="outline" size="sm" className="bg-[color:var(--surface)]"><Download className="w-4 h-4 mr-1" />Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => toast.promise(exportPNG("dashboard-capture", dashboard.name), { loading: "Rendering PNG\u2026", success: "PNG downloaded", error: "Export failed" })}><FileImage className="w-4 h-4 mr-2" />Export as PNG</DropdownMenuItem>
              <DropdownMenuItem onClick={() => toast.promise(exportPDF("dashboard-capture", dashboard.name), { loading: "Rendering PDF\u2026", success: "PDF downloaded", error: "Export failed" })}><FileText className="w-4 h-4 mr-2" />Export as PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <div className="hidden sm:flex items-center gap-1.5 text-xs text-[color:var(--ink-3)]">{dashboard.branding?.logo_url ? <img src={dashboard.branding.logo_url} alt="" className="w-4 h-4 rounded object-cover" /> : <Sparkles className="w-4 h-4 text-[color:var(--brand-ocean)]" />}{dashboard.branding?.app_name || "Eazy Analytics"}</div>
        </div>
      </header>
      {drillBar}
      {filterBar}
      {whatIfBar}
      {stage}
      {mobileFilters}
      <UnderlyingDataDialog open={!!underlying} onOpenChange={(v) => { if (!v) setUnderlying(null); }} ctx={underlying} />
    </div>
    </DashboardErrorBoundary>
  );
}
